import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, RefreshCw, FilePlus2, Copy } from "lucide-react";
import { endpoints } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { Card, Btn, handleErr } from "./AdminUI";

const inp = "h-9 px-3 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm outline-none focus:border-[#E11D2A]";
const Toggle = ({ on, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`h-8 px-4 rounded-full text-xs font-bold shrink-0 ${on ? "bg-emerald-500 text-black" : "bg-slate-600 text-white"}`}>{on ? "On" : "Off"}</button>
);

const UseRow = ({ k, label, u, save }) => {
  const [t, setT] = useState(u.template);
  const [l, setL] = useState(u.lang);
  return (
    <div className="flex flex-wrap items-center gap-2 py-2.5" data-testid={`wa-use-${k}`}>
      <span className="text-white text-sm font-semibold w-44">{label}</span>
      <input className={`${inp} w-48`} value={t} onChange={(e) => setT(e.target.value)} onBlur={() => t !== u.template && save({ uses: { [k]: { template: t } } })} data-testid={`wa-use-${k}-template`} />
      <input className={`${inp} w-20`} value={l} onChange={(e) => setL(e.target.value)} onBlur={() => l !== u.lang && save({ uses: { [k]: { lang: l } } })} data-testid={`wa-use-${k}-lang`} />
      <Toggle on={u.on} onClick={() => save({ uses: { [k]: { on: !u.on } } })} testid={`wa-use-${k}-toggle`} />
    </div>
  );
};

const Inbox = () => {
  const [threads, setThreads] = useState([]);
  const [sel, setSel] = useState(null);
  const [text, setText] = useState("");
  const load = () => endpoints.waInbox().then((r) => setThreads(r.data.threads || [])).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  const th = threads.find((x) => x.from === sel);
  const open = (f) => { setSel(f); endpoints.waRead(f).then(load).catch(() => {}); };
  const send = async () => { try { await endpoints.waReply(sel, text); setText(""); load(); } catch (e) { handleErr(e); } };
  return (
    <Card data-testid="wa-inbox">
      <h3 className="text-white font-bold mb-3">Fans' WhatsApp messages</h3>
      {threads.length === 0 ? <p className="text-sm text-slate-400" data-testid="wa-inbox-empty">No messages yet. They appear here once the webhook is connected in Meta.</p> : (
        <div className="grid md:grid-cols-[240px_1fr] gap-3">
          <div className="divide-y divide-[#273042] border border-[#273042] rounded-lg max-h-[420px] overflow-y-auto">
            {threads.map((t) => (
              <button key={t.from} onClick={() => open(t.from)} data-testid={`wa-thread-${t.from}`} className={`w-full text-left px-3 py-2 ${sel === t.from ? "bg-white/5" : ""}`}>
                <div className="text-white text-sm font-semibold truncate">{t.name || `+${t.from}`}{t.unread > 0 && <span className="ml-2 px-1.5 rounded-full bg-[#E11D2A] text-[10px]">{t.unread}</span>}</div>
                <div className="text-xs text-slate-400 truncate">{t.messages[t.messages.length - 1]?.text}</div>
              </button>))}
          </div>
          {th ? (
            <div className="flex flex-col border border-[#273042] rounded-lg p-3 min-h-[300px]">
              <div className="flex-1 space-y-2 overflow-y-auto max-h-[340px]">
                {th.messages.map((m) => (
                  <div key={m.id} className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${m.dir === "in" ? "bg-[#1b2230] text-white" : "ml-auto bg-emerald-600 text-white"}`}>{m.text}</div>))}
              </div>
              {th.canReply ? (
                <div className="flex gap-2 mt-3">
                  <input className={`${inp} flex-1`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply…" data-testid="wa-reply-input" onKeyDown={(e) => e.key === "Enter" && text.trim() && send()} />
                  <Btn onClick={send} disabled={!text.trim()} data-testid="wa-reply-send"><Send className="w-4 h-4" /></Btn>
                </div>
              ) : <p className="text-xs text-amber-300 mt-3">More than 24 h since the last message — WhatsApp only allows templates now.</p>}
            </div>
          ) : <p className="text-sm text-slate-500 self-center">Pick a conversation.</p>}
        </div>
      )}
    </Card>
  );
};

export default function AdminWhatsApp() {
  const [c, setC] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [to, setTo] = useState("");
  const [use, setUse] = useState("receipt");
  const load = () => endpoints.waConfig().then((r) => setC(r.data)).catch(handleErr);
  const loadTpl = () => endpoints.waTemplates().then((r) => setTpl(r.data)).catch(handleErr);
  useEffect(() => { load(); loadTpl(); }, []);
  const save = (b) => endpoints.waSaveConfig(b).then((r) => setC(r.data)).catch(handleErr);
  const create = async () => { try { const { data } = await endpoints.waCreateTemplates(); toast.success(data.results.map((x) => `${x.name}: ${x.result}`).join(" · ")); loadTpl(); } catch (e) { handleErr(e); } };
  const test = async () => { try { const { data } = await endpoints.waTest({ to, use }); data.ok ? toast.success("Sent — check WhatsApp") : toast.error(data.info || "Not sent"); load(); } catch (e) { handleErr(e); } };
  if (!c) return null;
  const hook = `${BACKEND_URL}${c.webhookUrl}`;
  return (
    <div className="space-y-5" data-testid="admin-whatsapp">
      <div className="flex items-center gap-3">
        <MessageCircle className="w-7 h-7 text-emerald-400" />
        <div className="flex-1"><h1 className="text-2xl font-bold text-white">WhatsApp (Meta)</h1>
          <p className="text-sm text-slate-400">Your own WhatsApp Business number sends first; the old gateway is the backup.</p></div>
        <Toggle on={c.enabled} onClick={() => save({ enabled: !c.enabled })} testid="wa-master-toggle" />
      </div>
      <Card>
        <div className="grid sm:grid-cols-4 gap-2 text-sm" data-testid="wa-status">
          <div><span className="text-slate-400 block text-xs">Connection</span><span className={c.configured ? "text-emerald-400" : "text-[#EF4444]"}>{c.configured ? "Connected" : "Missing token / number"}</span></div>
          <div><span className="text-slate-400 block text-xs">Sent / failed</span><span className="text-white">{c.sent} / {c.failed}</span></div>
          <div className="sm:col-span-2"><span className="text-slate-400 block text-xs">Last error</span><span className="text-amber-300 text-xs break-words" data-testid="wa-last-error">{c.lastError || "—"}</span></div>
        </div>
      </Card>
      <Card>
        <h3 className="text-white font-bold mb-1">What WhatsApp sends</h3>
        <p className="text-xs text-slate-400 mb-2">Template name and language must match an APPROVED template in your WhatsApp account.</p>
        <div className="divide-y divide-[#273042]">{Object.entries(c.uses).map(([k, u]) => <UseRow key={k} k={k} label={c.labels[k]} u={u} save={save} />)}
          <div className="flex items-center gap-2 py-2.5" data-testid="wa-use-inbox"><span className="text-white text-sm font-semibold flex-1">Receive fans' messages (inbox below)</span><Toggle on={c.inbox} onClick={() => save({ inbox: !c.inbox })} testid="wa-inbox-toggle" /></div>
        </div>
      </Card>
      <Card>
        <div className="flex items-center gap-2 mb-2"><h3 className="text-white font-bold flex-1">Templates in your WhatsApp account</h3>
          <Btn variant="ghost" onClick={loadTpl} data-testid="wa-templates-refresh"><RefreshCw className="w-4 h-4" /></Btn>
          <Btn onClick={create} data-testid="wa-templates-create"><FilePlus2 className="w-4 h-4" /> Create missing</Btn></div>
        {tpl?.error ? <p className="text-sm text-amber-300" data-testid="wa-templates-error">{tpl.error}</p> : (
          <div className="text-sm divide-y divide-[#273042]" data-testid="wa-templates">
            {(tpl?.items || []).length === 0 ? <p className="text-slate-400">No templates yet.</p> : tpl.items.map((t) => (
              <div key={`${t.name}-${t.language}`} className="flex gap-2 py-1.5"><span className="text-white flex-1">{t.name}</span><span className="text-slate-400">{t.language}</span>
                <span className={t.status === "APPROVED" ? "text-emerald-400" : "text-amber-300"}>{t.status}</span></div>))}
          </div>)}
      </Card>
      <Card>
        <h3 className="text-white font-bold mb-2">Send a test</h3>
        <div className="flex flex-wrap gap-2">
          <input className={`${inp} w-48`} value={to} onChange={(e) => setTo(e.target.value)} placeholder="2507XXXXXXXX" data-testid="wa-test-to" />
          <select className={inp} value={use} onChange={(e) => setUse(e.target.value)} data-testid="wa-test-use">{Object.entries(c.labels).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <Btn onClick={test} disabled={to.replace(/\D/g, "").length < 10} data-testid="wa-test-send"><Send className="w-4 h-4" /> Send test</Btn>
        </div>
      </Card>
      <Card>
        <h3 className="text-white font-bold mb-1">Webhook (to receive messages)</h3>
        <p className="text-xs text-slate-400 mb-2">In Meta → your app → WhatsApp → Configuration, paste these and subscribe to the "messages" field.</p>
        {[["Callback URL", hook, "wa-webhook-url"], ["Verify token", c.verifyToken, "wa-verify-token"]].map(([l, v, id]) => (
          <div key={id} className="flex items-center gap-2 py-1"><span className="text-slate-400 text-xs w-24">{l}</span><code className="text-white text-xs break-all flex-1" data-testid={id}>{v}</code>
            <button onClick={() => { navigator.clipboard.writeText(v); toast.success("Copied"); }} className="text-slate-400 hover:text-white"><Copy className="w-4 h-4" /></button></div>))}
        {!c.appSecretSet && <p className="text-xs text-[#EF4444] mt-1">App Secret missing — incoming messages will be rejected.</p>}
      </Card>
      {c.inbox && <Inbox />}
    </div>
  );
}
